import { useEffect, useState } from 'react';
import {
  MdFullscreen,
  MdFullscreenExit,
  MdPause,
  MdPlayArrow,
  MdVolumeOff,
  MdVolumeUp,
} from 'react-icons/md';

function formatTime(seconds) {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60).toString().padStart(2, '0');
  return `${minutes}:${remainingSeconds}`;
}

export default function PostVideoControls({
  videoRef,
  mediaRef,
  onTogglePlayback,
}) {
  const [mediaState, setMediaState] = useState({
    currentTime: 0,
    duration: 0,
    isFullscreen: false,
    isMuted: false,
    isPlaying: false,
    volume: 1,
  });

  useEffect(() => {
    const video = videoRef.current;
    const mediaElement = mediaRef.current;
    if (!video || !mediaElement) return undefined;

    const syncMediaState = () => {
      setMediaState({
        currentTime: Number.isFinite(video.currentTime) ? video.currentTime : 0,
        duration: Number.isFinite(video.duration) ? video.duration : 0,
        isFullscreen: document.fullscreenElement === mediaElement,
        isMuted: video.muted,
        isPlaying: !video.paused && !video.ended,
        volume: video.volume,
      });
    };

    const events = [
      'durationchange',
      'ended',
      'loadedmetadata',
      'pause',
      'play',
      'timeupdate',
      'volumechange',
    ];

    events.forEach(event => video.addEventListener(event, syncMediaState));
    document.addEventListener('fullscreenchange', syncMediaState);
    syncMediaState();

    return () => {
      events.forEach(event => video.removeEventListener(event, syncMediaState));
      document.removeEventListener('fullscreenchange', syncMediaState);
    };
  }, [mediaRef, videoRef]);

  const seek = event => {
    const video = videoRef.current;
    const nextTime = Number(event.target.value);
    if (!video || !Number.isFinite(nextTime)) return;

    video.currentTime = nextTime;
    setMediaState(current => ({ ...current, currentTime: nextTime }));
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
  };

  const updateVolume = event => {
    const video = videoRef.current;
    const nextVolume = Number(event.target.value);
    if (!video || !Number.isFinite(nextVolume)) return;

    video.volume = nextVolume;
    video.muted = nextVolume === 0;
  };

  const toggleFullscreen = async () => {
    const mediaElement = mediaRef.current;
    const video = videoRef.current;
    if (!mediaElement || !video) return;

    if (document.fullscreenElement) {
      await document.exitFullscreen?.();
      return;
    }

    if (mediaElement.requestFullscreen) {
      await mediaElement.requestFullscreen();
      return;
    }

    video.webkitEnterFullscreen?.();
  };

  const progress = mediaState.duration
    ? Math.min(100, (mediaState.currentTime / mediaState.duration) * 100)
    : 0;
  const currentVolume = mediaState.isMuted ? 0 : mediaState.volume;

  return (
    <div
      className="post-video-controls"
      onClick={event => event.stopPropagation()}
      onDoubleClick={event => event.stopPropagation()}
    >
      <button
        type="button"
        className="post-video-controls__button"
        aria-label={mediaState.isPlaying ? 'Pause video' : 'Play video'}
        onClick={onTogglePlayback}
      >
        {mediaState.isPlaying ? <MdPause /> : <MdPlayArrow />}
      </button>

      <span className="post-video-controls__time" aria-live="off">
        {formatTime(mediaState.currentTime)} / {formatTime(mediaState.duration)}
      </span>

      <input
        type="range"
        className="post-video-controls__progress"
        aria-label="Video progress"
        min="0"
        max={mediaState.duration || 0}
        step="0.01"
        value={Math.min(mediaState.currentTime, mediaState.duration || 0)}
        style={{ '--progress': `${progress}%` }}
        onChange={seek}
      />

      <div className="post-video-controls__volume">
        <button
          type="button"
          className="post-video-controls__button"
          aria-label={mediaState.isMuted ? 'Unmute video' : 'Mute video'}
          onClick={toggleMute}
        >
          {mediaState.isMuted || mediaState.volume === 0 ? <MdVolumeOff /> : <MdVolumeUp />}
        </button>
        <input
          type="range"
          className="post-video-controls__volume-range"
          aria-label="Video volume"
          min="0"
          max="1"
          step="0.05"
          value={currentVolume}
          onChange={updateVolume}
        />
      </div>

      <button
        type="button"
        className="post-video-controls__button"
        aria-label={mediaState.isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        onClick={() => {
          toggleFullscreen().catch(() => {});
        }}
      >
        {mediaState.isFullscreen ? <MdFullscreenExit /> : <MdFullscreen />}
      </button>
    </div>
  );
}
